/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint-disable no-param-reassign */
import { cloneDeep, uniq, uniqBy, flatten } from 'lodash-es';

import toast from '../../../producer/toast/toast';
import sportradarModule from '../sportradar/sportradar';
import SportradarService from '../../../services/sportradar';

import Analytics from '../../../utils/analytics';

import ProductionService from '../../../services/productions';
import GraphicsService from '../../../services/graphics';

import Reactive from '../../../utils/reactive';

import {
  shortenPanelUrls,
  shortenGraphicUrls,
} from '../../../utils/shortener';

import {
  // Helpers
  diffpatcher,
  getItemFromTimeline,
  isAlreadyQueued,
  determineSubIndex,
  validateTimelineItem,
  getPreviousItemOrPost,
  getNextItemOrPost,
  syncQueuedPost,
  isTheme,
  getSocialPosts,

  // Set/update prevProduction for tracking changes
  setPrevProduction,
  updatePrevProduction,

  // Socket emitters
  updateTimeline,
  sendSettings,
} from '../../../utils/production';

const catchify = require('catchify');

const { v1: uuidv1 } = require('uuid');

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const state = {
  loading: false,
  loadingMore: false,

  duration: 0,
  videoDuration: 0,
  production: {},
  totalActiveProductions: 0,
  productions: [],
  pagedProductions: [],
  flaggedProductions: [],
  deletedProductions: [],
  loadedDeletedProductions: false,
  selectedProductionId: null,
  tagsForUser: [],
  clientsInDetailedProduction: null,
  saving: false,
  lastUpdated: null,
  // Track timeline items with SR game elements
  activeSportradarInGameElements: {},

  // Pagination
  page: 1,
  limit: 50,
};

const getTimelineTemplates = async (owner, timeline, timelineIds) => {
  if (timelineIds && timelineIds.length) {
    // Only include items in list of timelineIds (and parents of the items if in a group)
    timeline = timeline.filter((item) => (
      timelineIds.includes(item.timeline_id)
        || item.posts.find((p) => timelineIds.includes(p?.timeline_id))
    ));
  }

  // NOTE: I don't like having to fetch all the graphic data just to shorten these URLs
  // Get list of templateIds for all graphics in the timeline
  // We fetch ALL templates in the timeline jic a new QR code was added that hasn't been shortened
  const templateIds = uniq(timeline.reduce((memo, post) => {
    const { layout } = post;

    switch (layout) {
      case 'graphic': {
        return memo.concat([post.template_id]);
      }

      case 'graphics': {
        return memo.concat(post.posts.map((p) => p.template_id));
      }

      default: {
        return memo;
      }
    }
  }, []));

  const templates = templateIds.length
    ? await GraphicsService.getTemplates(owner, templateIds)
    : [];
  return { templates, timeline };
};

const actions = {
  clearProduction(context) {
    // Make sure to leave the socket room before clearing the production.
    // Otherwise we'll lose the ID and be stuck in the room
    vm.$socket.emit('leave', { production_id: context.state.production._id });

    context.commit('CLEAR_PRODUCTION');
  },

  /**
   * Replace playlist item at index with provided elements(s)
   */
  replacePlaylistItem(context, payload) {
    context.commit('REPLACE_PLAYLIST_ITEM', payload);
  },

  /**
   * Replace playlist item at index withing a group with provided elements(s)
   */
  replaceGroupItem(context, payload) {
    context.commit('REPLACE_GROUP_ITEM', payload);
  },

  updateFlaggedProductions({ state, commit }, payload) {
    if (!payload.flagged) {
      // filter out all that don't match
      const remainingFlagged = state.flaggedProductions.filter((item) => item._id !== payload._id);
      commit('SET_FLAGGED_PRODUCTIONS', remainingFlagged);
    } else {
      // Add returned production to list of flagged productions
      const mergedFlagged = [payload, ...state.flaggedProductions];

      // Just incase something weird happens make sure we don't add a duplicate
      commit('SET_FLAGGED_PRODUCTIONS', uniqBy(mergedFlagged, (p) => p._id));
    }
  },

  addNewProduction({ state, commit, dispatch }, payload) {
    // merge in payload
    const mergedProductions = [payload, ...state.pagedProductions];

    // update the total number of records
    commit('SET_TOTAL_ACTIVE_PRODUCTIONS', state.totalActiveProductions + 1);

    // just incase something weird happens make sure we don't add a duplicate
    const ids = mergedProductions.map((o) => o._id);

    const filtered = mergedProductions.filter(({ _id }, index) => !ids.includes(_id, index + 1));
    commit('SET_PRODUCTIONS_PAGED', filtered);
    dispatch('updateFlaggedProductions', payload);
  },

  socket_clientsInProduction({ commit }, payload) {
    commit('UPDATE_CLIENTS_IN_DETAILED_PRODUCTION', payload.clientList);
  },

  socket_reconnect({ state }) {
    vm.$socket.emit('production', { production_id: state.production._id });
  },

  socket_timeline({ state, commit, dispatch }, payload) {
    if ('preview' in payload) {
      // All we really care about is what timeline item is set to preview
      // So this will make sure preview.index and preview.sub_index are set
      commit('PREVIEW_SET_SUB_INDEX', payload);
      return;
    }

    const isObject = (it) => typeof it === 'object';

    // Check the delta to see if we need to store some posts in the lookup table
    const deltaTimeline = Object
      .values(payload?.delta?.timeline || [])
      .filter(isObject);

    if (deltaTimeline.length) {
      deltaTimeline.forEach((item) => {
        // eslint-disable-next-line no-underscore-dangle
        if (item?.posts?._t === 'a') { // _t === 'a' means posts were added
          const posts = Object
            .values(item.posts)
            .filter(isObject);

          // Posts were added to timeline
          dispatch('addPostsToLookupTable', flatten(posts));
        }
      });
    }

    // Merge any deltas into current state
    commit('SET_TIMELINE', payload);

    const { timeline, index, sub_index: subIndex } = state.production;

    // Sync `queuedPost` on current live item if necessary
    if (syncQueuedPost({ timeline, index, subIndex })) {
      // `queuedPost` was updated, so make sure that change is sent to other clients
      dispatch('updateTimeline');
    }

    // Load any missing timeline items
    dispatch('getTimelineAssets');
  },

  socket_timelineSettings(context, payload) {
    const mutation = payload.preview ? 'PREVIEW_TIMELINE_SETTINGS' : 'TIMELINE_SETTINGS';
    context.commit(mutation, payload);
  },

  socket_startPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', true);
  },

  socket_stopPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', false);
  },

  socket_playbackDuration(context, duration) {
    context.commit('SET_DURATION', duration);
  },

  socket_videoDuration(context, payload) {
    const { duration, preview } = payload;

    if (preview) {
      context.commit('SET_PREVIEW_VIDEO_DURATION', duration);;
    } else {
      context.commit('SET_VIDEO_DURATION', duration);
    }
  },

  /**
   * Call out to other functions that are responsible for grabbing all assets
   * from the timeline and ensuring the necessary data is loaded from the API
   * and stored in their respective lookup tables.
   * Otherwise, items in the playlist could show up as "deleted".
   * Methods should not refetch data that we already have, so it should be safe to call multiple times.
   */
  getTimelineAssets({ dispatch }) {
    dispatch('getTimelineThemes');
    dispatch('getTimelinePanels');
    dispatch('getTimelineTemplates');
    dispatch('getTimelineTagboards');
    dispatch('getTimelinePosts');
  },

  advance(context, direction) {
    const {
      timeline,
      index,
      sub_index: subIndex,
      looped,
    } = context.state.production;

    const currentItemIdx = timeline.findIndex((i) => i.timeline_id === index);

    if (currentItemIdx === -1) {
      // Nothing is live, so there's nowhere to go
      return;
    }

    const opts = {
      timeline,
      index,
      subIndex,
      looped,
    };

    const moveTo = direction === 'previous'
      ? getPreviousItemOrPost(opts)
      : getNextItemOrPost(opts);

    // Handle updating index/sub_index
    context.commit('ADVANCE', moveTo);

    // If moving to another social graphic or theme, then update queuedPost.
    syncQueuedPost({ timeline, ...moveTo });

    // Send changes over the socket
    context.dispatch('updateTimeline');
  },

  updateTimeline(context) {
    // Ensure sub_index is set to the proper element
    context.dispatch('syncSubIndex');

    // Then send out changes to other clients
    context.commit('UPDATE_TIMELINE', { rootState: context.rootState });
  },

  setLayouts(context, layouts) {
    context.commit('SET_LAYOUTS', { layouts, rootState: context.rootState });
  },

  addLayouts(context, payload) {
    context.commit('ADD_LAYOUTS', { ...payload, rootState: context.rootState });

    // Ensure short URLs are generated for all new elements
    const timelineIds = payload.items.map((item) => item.timeline_id);
    context.dispatch('generateShortUrls', timelineIds);
    context.dispatch('trackSportradarInGameElement', timelineIds);
  },

  setPosts(context, payload) {
    context.commit('SET_POSTS', {
      rootState: context.rootState,
      ...payload,
    });
  },

  toggleLive(context, payload) {
    const { timelineId } = payload;

    const preview = context.rootState.preview.production;
    const { production } = context.state;

    const isAlreadyLive = (production.index === timelineId);
    const event = isAlreadyLive ? 'Off' : 'Live';
    const item = getItemFromTimeline(timelineId, production.timeline);

    context.commit('SET_SUB_INDEX', {
      index: isAlreadyLive ? null : timelineId,
      sub_index: isAlreadyLive ? null : determineSubIndex(item),
    });

    // If nothing is live anymore...
    if (isAlreadyLive) {
      // Pause any posts that were playing
      context.dispatch('stopPostVideo');

      // Pause auto-advance
      context.dispatch('pauseTimeline');
    } else if (preview.index === timelineId && preview.videoPlaying !== production.videoPlaying) {
      // If preview is already playing,
      // then copy videoPlaying flag from there to keep video state consistent.
      context.dispatch('setVideoPlaying', preview.videoPlaying);
    }

    context.dispatch('updateTimeline');

    Analytics.toggleLiveEvent({
      event,
      item,
      rootState: context.rootState,
    });
  },

  /**
   * Force sub_index to sync with current live element
   */
  syncSubIndex(context) {
    const {
      index,
      timeline,
      playing,
    } = context.state.production;

    // Don't sync anything while timeline is playing
    // Otherwise queuing a theme post in one timeline item might cause the live one to update too.
    if (playing) { return; }

    if (!index || index === '0') {
      // Reset both
      context.commit('SET_SUB_INDEX', {
        index: null,
        sub_index: null,
      });

      return;
    }

    const item = getItemFromTimeline(index, timeline);
    context.commit('SET_SUB_INDEX', {
      index,
      sub_index: determineSubIndex(item),
    });
  },

  toggleLivePost(context, payload) {
    const { rootState } = context;
    const { parentTimelineId, item } = payload;

    const {
      _id: productionId,
      index,
      sub_index: subIndex,
      timeline,
      owner,
    } = context.state.production;

    const post = rootState.posts.lookupTable[item.post_id];
    const postTimelineId = item.timeline_id;

    const isLayoutLive = (index === parentTimelineId);
    const isQueued = isAlreadyQueued(parentTimelineId, postTimelineId, timeline);

    if (isLayoutLive) {
      const isPostAlreadyLive = (subIndex === postTimelineId);
      const event = isPostAlreadyLive ? 'Off' : 'On';

      Analytics.log({
        event: `Producer Toggled Live Post ${event}`,
        fields: {
          production_id: productionId,
          post_id: item.post_id,
          network: item.network,
          permalink: post?.permalink,
        },
        ownerId: owner,
      });

      context.commit('SET_SUB_INDEX', {
        index: parentTimelineId,
        sub_index: isPostAlreadyLive ? null : postTimelineId,
      });
    }

    // Sync sub_index with queuedPost field on theme.
    // If post is already queued, then unqueue it.
    syncQueuedPost({
      timeline,
      index: parentTimelineId,
      subIndex: isQueued ? null : postTimelineId,
    });

    // Trigger updateTimeline if something above changed the state.
    if (isLayoutLive || !isQueued) {
      context.dispatch('updateTimeline');
    }
  },

  toggleLiveVideo(context) {
    const { production } = context.state;

    if (production.videoPlaying) {
      context.commit('SET_VIDEO_PLAYING', false);
    } else {
      context.commit('SET_VIDEO_PLAYING', true);
    }

    context.dispatch('sendSettings');
  },

  playPostVideo(context) {
    context.commit('SET_VIDEO_PLAYING', true);
    context.dispatch('updateTimeline');
  },

  stopPostVideo(context) {
    context.commit('SET_VIDEO_PLAYING', false);
    context.dispatch('updateTimeline');
  },

  setVideoPlaying(context, videoPlaying) {
    context.commit('SET_VIDEO_PLAYING', videoPlaying);
    context.dispatch('updateTimeline');
  },

  playTimeline(context) {
    Analytics.log({
      event: 'Producer Played Playlist',
      fields: { production_id: context.state.production._id },
      ownerId: context.state.production.owner,
    });

    const {
      index,
      sub_index: subIndex,
      timeline,
    } = context.state.production;

    const item = timeline.find((it) => it.timeline_id === index);

    // If currently on a theme without a queued post,
    // then queue the first post in the list
    if (isTheme(item?.layout) && item.posts?.length) {
      // Check that queued post exists
      const hasQueuedPost = item.queuedPost && item.posts.find((p) => p?.timeline_id === item.queuedPost);

      // If there's no queued post, or it was deleted,
      // then set queuedPost back to first post in list.
      if (!hasQueuedPost) {
        const [post] = item.posts;

        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    const isGraphic = ['graphic', 'graphics'].includes(item?.layout);

    // Auto-select first post in social graphic if nothing is already queued
    // `index` will match `sub_index` if no nested posts are queued
    if (isGraphic && (!subIndex || index === subIndex)) {
      const posts = getSocialPosts(item);

      if (posts?.length) {
        const [post] = posts;

        // Make sure the `queuedPost` field is updated on the live graphic
        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    context.commit('PLAY_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are enabled
    context.dispatch('playPostVideo');
    context.dispatch('playPreviewVideo');

    context.dispatch('sendSettings');
  },

  pauseTimeline(context) {
    Analytics.log({
      event: 'Producer Paused Playlist',
      fields: { production_id: context.state.production._id },
      ownerId: context.state.production.owner,
    });

    context.commit('PAUSE_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are disabled
    context.dispatch('stopPostVideo');
    context.dispatch('stopPreviewVideo');

    context.dispatch('sendSettings');
  },

  togglePostVideo(context) {
    context.commit('TOGGLE_VIDEO_PLAYING');
    context.dispatch('sendSettings');
  },

  sendSettings(context) {
    sendSettings(context.state, context.rootState);

    // Since we aren't watching the production anymore,
    // settings changes need to be saved manually
    context.dispatch('updateProduction', context.state.production);
  },

  removeTimelineItem(context, { timelineId }) {
    const { index, timeline } = context.state.production;

    const activeSportradarInGameTimelineIds = Object.keys(context.state.activeSportradarInGameElements);
    timeline.forEach((item) => {
      if (item.layout === 'graphics' && item.timeline_id === timelineId) { // Graphic group being removed
        const { posts } = item;
        posts.forEach((post) => {
          if (activeSportradarInGameTimelineIds.includes(post.timeline_id)) {
            context.dispatch('removeActiveSportradarInGameElements', post.timeline_id);
          }
        });
      }
    });

    if (activeSportradarInGameTimelineIds.includes(timelineId)) {
      context.dispatch('removeActiveSportradarInGameElements', timelineId);
    }

    context.commit('REMOVE_LAYOUT', { timelineId });

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');

    if (!timeline.length || index === timelineId) {
      // Make sure the timeline isn't still playing
      context.dispatch('pauseTimeline');
      context.dispatch('stopPostVideo');
    }
  },

  addGraphicsGroup({ commit, rootState }, payload) {
    commit('ADD_GRAPHICS_GROUP', { ...payload, rootState });
  },

  // Merge two graphics at respective indices into one "graphics" component
  mergeGraphics(context, payload) {
    context.commit('MERGE_GRAPHICS', payload);

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');
  },

  duplicateTimelineItem(context, payload) {
    const tmp = cloneDeep(payload.item);
    tmp.title += ' - COPY';
    tmp.timeline_id = uuidv1();

    // If element is a group, we need to generate new timeline_ids for all children
    if (tmp.layout === 'graphics') {
      tmp.posts.forEach((i) => {
        const item = i;
        item.timeline_id = uuidv1();
      });
    }

    context.commit('DUPLICATE_LAYOUT', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  duplicateTimelineItemInGroup(context, payload) {
    const tmp = cloneDeep(payload.item);
    tmp.title += ' - COPY';
    tmp.timeline_id = uuidv1();
    context.commit('DUPLICATE_LAYOUT_IN_GROUP', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  removeThemePost(context, payload) {
    const {
      index,
      sub_index: subIndex,
      playing,
    } = context.state.production;

    // If removing post that is currently live, and we are playing,
    // then trigger an advance first
    if (index === payload.index && subIndex === payload.sub_index && playing) {
      context.dispatch('advance');
    }

    // Remove the actual post from the timeline theme
    context.commit('REMOVE_POST', payload);

    // Finally, let other clients know what we just did
    context.dispatch('updateTimeline');
  },

  clearTimeline(context) {
    Analytics.log({
      event: 'Producer Cleared Playlist',
      fields: { production_id: context.state.production._id },
      ownerId: context.state.production.owner,
    });

    context.commit('CLEAR_TIMELINE', { rootState: context.rootState });
  },

  toggleMute(context) {
    const { muted } = context.state.production;

    Analytics.log({
      event: `Producer ${muted ? 'Unmuted' : 'Muted'} Playlist`,
      fields: { production_id: context.state.production._id },
      ownerId: context.state.production.owner,
    });

    context.commit('TOGGLE_MUTE', { rootState: context.rootState });
    context.dispatch('sendSettings');
  },

  toggleLoop(context) {
    const { looped } = context.state.production;

    Analytics.log({
      event: `Producer ${looped ? 'disable' : 'Enabled'} Playlist Loop`,
      fields: { production_id: context.state.production._id },
      ownerId: context.state.production.owner,
    });

    context.commit('TOGGLE_LOOP', { rootState: context.rootState });
    context.dispatch('sendSettings');
  },

  startLoader(context) {
    context.commit('START_LOADER');
  },

  stopLoader(context) {
    context.commit('STOP_LOADER');
  },

  incrementPage(context) {
    context.commit('INCREMENT_PAGE');
  },

  /**
   * Fetch list of productions (paginated).
   * @returns {Array<Object>} List of productions.
   */
  async getProductionsPaged({ state, commit, rootState }) {
    const { pagedProductions, page, limit } = state;
    const { id: owner } = rootState.user;

    commit('START_LOADER');

    try {
      const data = await ProductionService.getProductionsPaged({
        page,
        limit,
        owner,
      });

      let productions = data.data;

      if (page > 1) {
        productions = [...pagedProductions, ...productions];
      }

      // De-dupe jic there was a race to load more or something
      productions = uniqBy(productions, '_id');

      commit('SET_TOTAL_ACTIVE_PRODUCTIONS', data.totalCount);
      commit('SET_PRODUCTIONS_PAGED', productions);
      return data;
    } finally {
      commit('STOP_LOADER');
    }
  },

  async flagUpdateInProductions({ state, commit, dispatch }, payload) {
    commit('START_LOADER');

    const { _id: productionId } = payload;
    const flagged = !payload.flagged;
    const { id: userId } = this.state.user || {};

    const updateProductionIndex = state.pagedProductions.findIndex(
      (prod) => prod._id === productionId,
    );

    const response = await ProductionService.flagProduction(productionId, flagged, userId);

    if (updateProductionIndex !== -1) {
      commit('UPDATE_FLAG_IN_PAGED_PRODUCTIONS', updateProductionIndex);
    }

    dispatch('updateFlaggedProductions', response);
    commit('STOP_LOADER');
  },

  // Hitting the new API V2 endpoints
  getTagsForUser(context) {
    ProductionService.getTagsForUser(context.rootState.user.id).then((data) => {
      context.commit('GET_TAGS_FOR_USER', data);
    }).catch(() => {
      context.commit('STOP_LOADER');
    });
  },

  async getDeletedProductions(context) {
    context.commit('START_LOADER');

    const [err, data] = await catchify(
      ProductionService.searchProductions({
        owner: context.rootState.user.id,
        deleted: true,
      }),
    );

    if (err) {
      context.commit('STOP_LOADER');

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load deleted productions.',
      });

      throw err;
    }

    context.commit('SET_DELETED_PRODUCTIONS', data);
    context.commit('STOP_LOADER');
    return data;
  },

  async getFlaggedProductions(context) {
    const [err, data] = await catchify(
      ProductionService.searchProductions({
        owner: context.rootState.user.id,
        flagged: true,
      }),
    );

    if (err) {
      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load starred productions.',
      });

      throw err;
    }

    context.commit('SET_FLAGGED_PRODUCTIONS', data);
    return data;
  },

  // Hitting the new API V2 endpoints
  async getProductionV2({ state, commit }, productionId) {
    if (state.production.id && state.production.id > 0) {
      // First leave the socket room for the old production
      // This should probably be done whenever the user navigates away
      vm.$socket.emit('leave', { production_id: state.production.id });
    }

    const [err, data] = await catchify(
      ProductionService.getProduction(productionId),
    );

    if (err) {
      // Just catching the error to show a toast
      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load production',
      });

      // Bubble error up
      throw err;
    }

    data.id = data._id;

    commit('SET_PRODUCTION', data);

    return data;
  },

  addToActiveSportradarInGameElements(context, { timelineId, sport, gameId }) {
    const { state } = context;
    const sameGameItems = [];

    // track the number of elements subscribed to the gameId
    const keys = Object.keys(state.activeSportradarInGameElements);
    keys.forEach((key) => {
      const element = state.activeSportradarInGameElements[key];
      if (element?.gameIds?.includes(gameId)) {
        sameGameItems.push(key);
      }
    });

    // Subscribe to game channel if there are no existing subscriptions for that game
    if (!sameGameItems.length) {
      SportradarService.subscribeToGameChannel(sport, gameId);
    }

    // Handle state updates
    if (state.activeSportradarInGameElements[timelineId]) { // element already exists in state
      const timelineElement = state.activeSportradarInGameElements[timelineId];
      if (timelineElement?.gameIds && !timelineElement.gameIds.includes(gameId)) {
        timelineElement.gameIds.push(gameId);
      }
    } else if (!state.activeSportradarInGameElements[timelineId]) { // element not yet in state
      state.activeSportradarInGameElements[timelineId] = {
        sport,
        gameIds: [gameId],
      };
    }
  },

  removeActiveSportradarInGameElements(context, timelineId) {
    const { state } = context;
    const { sport, gameIds: removedGameIds } = state.activeSportradarInGameElements[timelineId];

    // First remove the current element
    delete state.activeSportradarInGameElements[timelineId];

    // Check if any other active SR game elements are subscribing to the same sport/gameId
    removedGameIds.forEach((removedGameId) => {
      const keys = Object.keys(state.activeSportradarInGameElements);
      const sameGameItems = [];
      keys.forEach((key) => {
        const timelineElement = state.activeSportradarInGameElements[key];
        if (timelineElement.gameIds.includes(removedGameId)) {
          sameGameItems.push(key);
        }
      });

      // Unsubscribe to game channel if there are no remaining subscriptions for that game
      if (!sameGameItems.length) {
        SportradarService.unsubscribeFromGameChannel(sport, removedGameId);
      }
    });
  },

  /**
   * Check if template has a SR In Game elements
   * Add to active list if so
   * * @param {Array<int>} [timelineIds]
   */
  async trackSportradarInGameElement(context, timelineIds) {
    const { state } = context;
    const { production } = state;
    const { owner, timeline: initialTimeline } = production;

    if (!initialTimeline) { return; }

    const { timeline, templates } = await getTimelineTemplates(owner, initialTimeline, timelineIds); // TODO: is this duplicating functionality from the same function name in the graphics store?

    const srGameElements = templates.reduce((results, template) => {
      const { elements, _id: templateId } = template;
      elements.forEach((element) => {
        if (element.type === 'sportradar' && element.data?.gameId?.length) { // "Game" elements only
          results.push({
            templateId,
            sport: element.data.sport,
            gameId: element.data.gameId,
          });
        }
      });

      return results;
    }, []);

    srGameElements.forEach((element) => {
      const { sport, gameId } = element;

      const srGameTimelineIds = [];

      timeline.forEach((item) => {
        if (!item.template_id) { // Graphics Group
          item.posts?.forEach((post) => {
            if (post.timeline_id && post?.template_id === element.templateId) {
              srGameTimelineIds.push(post.timeline_id);
            }
          });
        } else if (item.timeline_id && item.template_id === element.templateId) { // Single Graphic Template
          srGameTimelineIds.push(item.timeline_id);
        }
      });

      srGameTimelineIds.forEach((timelineId) => {
        context.dispatch('addToActiveSportradarInGameElements', { timelineId, sport, gameId });
      });
    });
  },

  /**
   * Loop over timeline items and generate short URLs
   * for any QR codes found that don't already have a short URL
   * @param {Array<int>} [timelineIds] - Limit short URL generation to provided list of items
   */
  async generateShortUrls(context, timelineIds) {
    const { state, rootState } = context;
    // Check if feature is enabled
    if (process.env.QR_CODE_SWITCH !== 'ON') { return; }

    const { production } = state;
    const { owner, timeline: initialTimeline } = production;

    if (!initialTimeline) { return; }

    const { timeline, templates } = await getTimelineTemplates(owner, initialTimeline, timelineIds);

    const opts = {
      production,
      timelineIds,
      panels: rootState.panels.lookupTable,
      templates,
    };

    // Loop over every timeline item and check if there are URLs in desparate need of shortening
    await Promise.all(
      timeline.map(async (post) => {
        switch (post.layout) {
          case 'panel': {
            await shortenPanelUrls({ ...opts, post });
            break;
          }

          case 'graphics': {
            await Promise.all(
              post.posts.map((p) => shortenGraphicUrls({ ...opts, post: p })),
            );

            break;
          }

          case 'graphic': {
            await shortenGraphicUrls({ ...opts, post });
            break;
          }

          default: break;
        }
      }),
    );
  },

  async createProduction({ dispatch }, production) {
    const data = await ProductionService.createProduction(production);
    dispatch('addNewProduction', data);
    return data;
  },

  async deleteProduction({ dispatch }, { production, trigger }) {
    const [err, data] = await catchify(
      ProductionService.deleteProduction(production._id),
    );

    if (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Failed to delete ${production.title}.`,
      });

      throw err;
    }

    dispatch('updateProductionListsAfterDelete', production);

    toast.makeToast({
      text: `${production.title} has been deleted.`,
    });

    Analytics.log({
      event: 'Producer Deleted Production',
      fields: {
        production_id: production._id,
        tags: production.tags,
        title: production.title,
        starred: production.flagged,
        trigger,
      },
      ownerId: production.owner,
    });

    return data;
  },
  updateProductionListsAfterDelete({ state, commit }, deletedProduction) {
    const newPagedProductions = state.pagedProductions.filter(
      (production) => production._id !== deletedProduction._id,
    );
    const newFlaggedProductions = state.flaggedProductions.filter(
      (production) => production._id !== deletedProduction._id,
    );
    const newDeletedProductions = [deletedProduction, ...state.deletedProductions];

    commit('SET_PRODUCTIONS_PAGED', newPagedProductions);
    commit('SET_TOTAL_ACTIVE_PRODUCTIONS', newPagedProductions.length);
    commit('SET_FLAGGED_PRODUCTIONS', newFlaggedProductions);
    commit('SET_DELETED_PRODUCTIONS', newDeletedProductions);
  },

  async restoreProduction({ dispatch }, { production, trigger }) {
    try {
      await ProductionService.restoreProduction(production._id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to restore production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Something went wrong. ${production.title} may not have been deleted. Refresh to confirm.`,
      });

      throw err;
    }

    dispatch('updateProductionListsAfterRestore', production);

    toast.makeToast({
      text: `${production.title} has been restored.`,
    });

    (() => {
      Analytics.log({
        event: 'Producer Restored Production',
        fields: {
          production_id: production._id,
          tags: production.tags,
          title: production.title,
          starred: production.flagged,
          trigger,
        },
        ownerId: production.owner,
      });
    })();
  },

  updateProductionListsAfterRestore({ state, commit }, restoredProduction) {
    const newPagedProductions = [restoredProduction, ...state.pagedProductions];
    const newDeletedProductions = state.deletedProductions.filter(
      (production) => production._id !== restoredProduction._id,
    );

    if (restoredProduction.flagged) {
      const newFlaggedProductions = [restoredProduction, ...state.flaggedProductions];
      commit('SET_FLAGGED_PRODUCTIONS', newFlaggedProductions);
    }

    commit('SET_PRODUCTIONS_PAGED', newPagedProductions);
    commit('SET_DELETED_PRODUCTIONS', newDeletedProductions);
  },

  updateProductionListsAfterEdit({ commit }, editedProduction) {
    commit('MERGE_PRODUCTION_EDITS', { edit: editedProduction });
  },

  async updateProduction(context, production) {
    await ProductionService.updateProduction(production);
    context.dispatch('updateProductionListsAfterEdit', production);
  },

  sendAction(context, payload) {
    vm.$socket.emit('handleAction', { production_id: this.state.production.production._id, action: payload });
  },

  registerDataModule(context, payload) {
    const { element } = payload;
    const { type } = element;

    if (type === 'sportradar') {
      if (!this.hasModule(element.id)) {
        // TODO: this is the same way sr data is stored in state with graphics for consistency
        // but it's not really necessary to store everything in state per element all of the time
        // notably when two of the same sport(nba) elements.
        this.registerModule(element.id, sportradarModule);
      }
    }
  },
};

const mutations = {
  INCREMENT_PAGE(state) {
    state.page += 1;
  },

  CLEAR_PRODUCTION(state) {
    state.production = {};
  },

  REPLACE_PLAYLIST_ITEM(state, payload) {
    state.production.timeline.splice(payload.index, 1, ...payload.items);
  },

  REPLACE_GROUP_ITEM(state, payload) {
    state.production.timeline[payload.groupIndex].posts.splice(payload.index, 1, ...payload.items);
  },

  GET_TAGS_FOR_USER(state, payload) {
    state.tagsForUser = payload;
  },

  SET_PRODUCTIONS_PAGED(state, payload) {
    state.pagedProductions = payload;
    // Storing this separately so we can update it client side without triggering an autosave
    state.lastUpdated = state.production.last_updated;
  },

  SET_FLAGGED_PRODUCTIONS(state, payload) {
    state.flaggedProductions = payload;
  },

  SET_DELETED_PRODUCTIONS(state, payload) {
    state.deletedProductions = payload;
    state.loadedDeletedProductions = true;
  },

  ADD_PRODUCTION_V2(state, payload) {
    state.pagedProductions.push(payload.production);
  },

  UPDATE_FLAG_IN_PAGED_PRODUCTIONS(state, index) {
    state.pagedProductions[index].flagged = !state.pagedProductions[index].flagged;
  },

  UPDATE_CLIENTS_IN_DETAILED_PRODUCTION(state, payload) {
    state.clientsInDetailedProduction = payload;
  },

  SET_TOTAL_ACTIVE_PRODUCTIONS(state, payload) {
    state.totalActiveProductions = payload;
  },

  SET_PRODUCTION(state, payload) {
    state.production = payload;
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);
    setPrevProduction(payload);
  },

  SET_TIMELINE(state, payload) {
    const { delta } = payload;

    // We only care if a delta is provided AND a production is still loaded in state
    if (!delta || !state.production) {
      return;
    }

    // If index or sub_index were updated in the delta, then reset video duration
    // This is so the timers properly hide when advancing from video to non-video
    if ('index' in delta || 'sub_index' in delta) {
      state.videoDuration = 0;
    }

    // Apply the delta to the current production
    try {
      diffpatcher.patch(state.production, delta);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error merging delta:', delta, 'into production:', state.production);
      throw err;
    }

    // Filter out invalid timeline items (null, undefined, etc.)
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);

    // Ensure any new properties added to the production are reactive
    // Sadly this is still necessary, because some elements won't update properly
    Reactive.createReactiveNestedObject(state.production, state.production);

    // Store record of "previous" production for future deltas
    setPrevProduction(state.production);

    state.lastUpdated = state.production.last_updated;
  },

  UPDATE_TIMELINE(state, payload) {
    updateTimeline(state, payload.rootState);
  },

  TIMELINE_SETTINGS(state, payload) {
    updatePrevProduction(payload);

    state.production = {
      ...state.production,
      ...payload,
    };
  },

  UPDATE_TIMELINE_THEME(state, payload) {
    const idx = state.production.timeline.findIndex((o) => o._id === payload.id);
    const themeIdx = payload.themes.findIndex((o) => o._id === payload.id);

    if (idx !== -1 && themeIdx !== -1) {
      Object.assign(state.production.timeline[idx], payload.themes[themeIdx]);
    }
  },

  SET_LAYOUTS(state, payload) {
    state.production.timeline = payload.layouts;
    updateTimeline(state, payload.rootState);
  },

  ADD_LAYOUTS(state, payload) {
    // Add to existing group
    if (payload.parentId) {
      const parent = state.production.timeline.find((i) => i.timeline_id === payload.parentId);
      if (!parent) {
        return;
      }

      parent.posts.push(...payload.items);
      updateTimeline(state, payload.rootState);
      return;
    }

    // Add straight to playlist
    state.production.timeline = [...state.production.timeline, ...payload.items];
    updateTimeline(state, payload.rootState);
  },

  REMOVE_LAYOUT(state, { timelineId }) {
    const parent = state.production.timeline.find((item) => (
      item.timeline_id !== timelineId
      && item.layout === 'graphics'
      && !!item.posts.find((child) => child.timeline_id === timelineId)
    ));

    // If item has a parent, just remove it from the group
    // and don't worry about checking if parent is live or not
    if (parent) {
      const index = parent.posts.findIndex((child) => child.timeline_id === timelineId);
      parent.posts.splice(index, 1);
      return;
    }

    const index = state.production.timeline.findIndex(
      (layout) => (
        layout.timeline_id === timelineId
      ),
    );

    state.production.timeline.splice(index, 1);

    // production.index is actually an UUID of the live element
    const isLayoutLive = (timelineId === state.production.index);
    state.production.index = isLayoutLive ? null : state.production.index;
    state.production.sub_index = isLayoutLive ? null : state.production.sub_index;
  },

  DUPLICATE_LAYOUT(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Index points to the location of the element being duplicated
    // or the index of the group that element is inside of
    const index = state.production.timeline.findIndex(
      (layout) => (
        layout.timeline_id === timelineId
        || !!layout.posts.find((i) => i.timeline_id === timelineId)
      ),
    );

    if (index !== -1) {
      state.production.timeline.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  DUPLICATE_LAYOUT_IN_GROUP(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Find the parent of the item being duplicated
    const parent = state.production.timeline.find((item) => (
      item.timeline_id !== timelineId
      && item.layout === 'graphics'
      && !!item.posts.find((child) => child.timeline_id === timelineId)
    ));

    if (!parent) {
      return;
    }

    // Find the index of the original element inside the group
    const index = parent.posts.findIndex(
      (child) => (
        child.timeline_id === timelineId
      ),
    );

    // Finally, splice in the clone right after the nested graphic in the group
    if (index !== -1) {
      parent.posts.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  TOGGLE_QUEUED_POST(state, payload) {
    const { timelineId, postId } = payload;
    const post = getItemFromTimeline(timelineId, state.production.timeline);

    // Only set queuedPost on the root element if it's a theme
    // Graphics keep queuedPost in the fields
    if (post && post.layout !== 'graphic') {
      post.queuedPost = postId;
    }
  },

  SET_POSTS(state, payload) {
    const idx = state.production.timeline.findIndex((i) => i.timeline_id === payload.timelineId);
    state.production.timeline[idx].posts = payload.posts;
    updateTimeline(state, payload.rootState);
  },

  REMOVE_POST(state, payload) {
    const { timeline } = state.production;

    const index = timeline.findIndex((o) => o.timeline_id === payload.index);
    const theme = timeline[index];

    // Lookup index only if provided sub_index is a timeline_id
    const postIndex = typeof payload.sub_index === 'number'
      ? payload.sub_index
      : theme.posts.findIndex((p) => p.timeline_id === payload.sub_index);

    if (payload.sub_index === state.production.sub_index) {
      state.production.sub_index = null;
    }

    theme.posts.splice(postIndex, 1);
  },

  ADD_GRAPHICS_GROUP(state, { rootState, index = -1, posts = [] }) {
    const numGroups = state.production.timeline.filter((i) => i.layout === 'graphics').length;

    const group = {
      title: `Graphics Group ${numGroups + 1}`,
      timeline_id: uuidv1(),
      displayTime: 5e3,
      transition: rootState.production.production.transition,
      layout: 'graphics',
      posts,
    };

    if (index === -1) {
      // Just append to end of timeline
      state.production.timeline.push(group);
    } else {
      // Splice into timeline at specific index
      state.production.timeline.splice(index, 0, group);
    }

    // Let connected clients know a group was added
    updateTimeline(state, rootState);
  },

  MERGE_GRAPHICS(state, { i1, i2 }) {
    const g1 = clone(state.production.timeline[i1]);
    const g2 = clone(state.production.timeline[i2]);

    const newIndex = Math.min(i1, i2);

    // Ensure elements are actually graphics or groups, in case something changed
    if (!['graphic', 'graphics'].includes(g1?.layout)) {
      return;
    }

    if (!['graphic', 'graphics'].includes(g2?.layout)) {
      return;
    }

    if ((g1.layout === 'graphics' && g2.layout === 'graphic') || (g1.layout === 'graphic' && g2.layout === 'graphics')) {
      // Add graphic to existing group, remove leftover graphic
      if (g1.layout === 'graphics') {
        // Append graphic 2 to group 1
        g1.posts.push(g2);
        state.production.timeline.splice(i1, 1, g1);
        state.production.timeline.splice(i2, 1);
      } else {
        // Prepend graphic 1 to group 2
        g2.posts.unshift(g1);
        state.production.timeline.splice(i2, 1, g2);
        state.production.timeline.splice(i1, 1);
      }

      return;
    }

    if (g1.layout === 'graphics' && g2.layout === 'graphics') {
      // Merge two different groups together, taking group 1 properties
      g1.posts = g1.posts.concat(g2.posts);
      state.production.timeline.splice(i1, 1, g1);
      state.production.timeline.splice(i2, 1);
      return;
    }

    // Two single graphics, create a new group and add both
    // New group is inserted at i1
    state.production.timeline.splice(i1, 1);
    state.production.timeline.splice(i2 > i1 ? i2 - 1 : i2, 1);

    const numGroups = state.production.timeline.filter((i) => i.layout === 'graphics').length;

    const item = {
      title: `Graphics Group ${numGroups + 1}`,
      timeline_id: uuidv1(),
      owner: g1.owner,
      displayTime: g1.displayTime,
      transition: g1.transition,
      layout: 'graphics',
      posts: [g1, g2],
    };

    // Insert new component
    state.production.timeline.splice(newIndex, 0, item);
  },

  MERGE_PRODUCTION_EDITS(state, payload) {
    const { edit } = payload;

    const prodIdx = state.pagedProductions.findIndex((p) => p._id === edit._id);
    if (prodIdx >= 0) {
      Object.assign(state.pagedProductions[prodIdx], edit);
    }

    const flaggedIdx = state.flaggedProductions.findIndex((p) => p._id === edit._id);
    if (flaggedIdx >= 0) {
      Object.assign(state.flaggedProductions[flaggedIdx], edit);
    }
  },

  CLEAR_TIMELINE(state, payload) {
    state.production.timeline = [];
    state.production.sub_index = null;
    state.production.index = null;
    state.production.playing = false;
    state.production.videoPlaying = false;
    state.videoDuration = 0;

    updateTimeline(state, payload.rootState);
  },

  PREVIEW_UPDATE_TIMELINE(state, payload) {
    const preview = {
      index: payload.index,
      sub_index: payload.sub_index,
      credentials: payload.rootState.preview.credentials,
    };

    updateTimeline(state, payload.rootState, preview);
  },

  SET_SUB_INDEX(state, payload) {
    const { production } = state;
    const { index, sub_index: subIndex } = payload;

    // Also clear video duration so the UI will go away
    // until the output tells us about the new video (if there is one)
    // But only if the selected item changed
    if (production.index !== index || production.sub_index !== subIndex) {
      state.videoDuration = 0;
    }

    state.production.index = index;
    state.production.sub_index = subIndex;
  },

  ADVANCE(state, payload) {
    const { index, subIndex } = payload;
    const { production } = state;

    production.index = index;
    production.sub_index = subIndex;

    // Clear videoDuration when changing active item
    state.videoDuration = 0;
  },

  PLAY_TIMELINE(state) {
    state.production.playing = true;
  },

  PAUSE_TIMELINE(state) {
    state.production.playing = false;
  },

  SET_VIDEO_PLAYING(state, videoPlaying) {
    state.production.videoPlaying = videoPlaying;
  },

  TOGGLE_VIDEO_PLAYING(state) {
    state.production.videoPlaying = !state.production.videoPlaying;
  },

  SET_DURATION(state, payload) {
    state.duration = payload;
  },

  SET_VIDEO_DURATION(state, payload) {
    state.videoDuration = payload;
  },

  TOGGLE_MUTE(state) {
    state.production.muted = !state.production.muted;
  },

  TOGGLE_LOOP(state) {
    state.production.looped = !state.production.looped;
  },

  START_LOADER(state) {
    state.loading = true;
  },

  STOP_LOADER(state) {
    state.loading = false;
  },

  SET_SAVING_FLAG(state, payload) {
    state.saving = payload;
  },
};

const getters = {
  /* eslint-disable no-shadow */
  isSaving(state) {
    return state.saving;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
